<template>
  <div class="container-fluid">
    <!-- Header -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="d-flex justify-content-between align-items-center">
          <div>
            <h2 class="mb-1">
              <i class="fas fa-robot me-2"></i>
              {{ $t("aiModelContext.title") }}
            </h2>
            <p class="text-muted mb-0">
              {{ $t("aiModelContext.subtitle") }}
            </p>
          </div>
          <div>
            <button
              @click="$router.push('/gotrading/prompt-settings')"
              class="btn btn-primary"
            >
              <i class="fas fa-cog me-1"></i>
              {{ $t("aiModelContext.configurePrompts") }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Información -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="alert alert-info">
          <h6>
            <i class="fas fa-info-circle me-2"></i>{{ $t("aiModelContext.whatIsAIContext") }}
          </h6>
          <p class="mb-2">
            {{ $t("aiModelContext.aiContextDescription") }}
          </p>
          <ul class="mb-0">
            <li>
              <strong>{{ $t("aiModelContext.whatModelRepresents") }}</strong> - {{ $t("aiModelContext.whatModelRepresentsDesc") }}
            </li>
            <li>
              <strong>{{ $t("aiModelContext.howToInterpretData") }}</strong> - {{ $t("aiModelContext.howToInterpretDataDesc") }}
            </li>
            <li>
              <strong>{{ $t("aiModelContext.whatToLookFor") }}</strong> - {{ $t("aiModelContext.whatToLookForDesc") }}
            </li>
            <li>
              <strong>{{ $t("aiModelContext.businessContext") }}</strong> - {{ $t("aiModelContext.businessContextDesc") }}
            </li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Lista de Modelos -->
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-header">
            <h5 class="mb-0">
              <i class="fas fa-list me-2"></i>
              {{ $t("aiModelContext.definitionModels") }}
            </h5>
          </div>
          <div class="card-body">
            <ContentSkeleton
              v-if="loading"
              variant="table"
              :rows="6"
              :cols="5"
              :label="$t('aiModelContext.loadingModels')"
            />

            <div v-else-if="models.length === 0" class="text-center py-4">
              <i class="fas fa-folder-open fa-3x text-muted mb-3"></i>
              <h5>{{ $t("aiModelContext.noModelsDefined") }}</h5>
              <p class="text-muted">
                {{ $t("aiModelContext.createModelsDescription") }}
              </p>
              <button
                @click="$router.push('/gotrading/model-definitions')"
                class="btn btn-primary"
              >
                <i class="fas fa-plus me-1"></i>
                {{ $t("aiModelContext.createModel") }}
              </button>
            </div>

            <div v-else class="table-responsive">
              <table class="table table-hover">
                <thead>
                  <tr>
                    <th>{{ $t("aiModelContext.model") }}</th>
                    <th>{{ $t("aiModelContext.label") }}</th>
                    <th>{{ $t("aiModelContext.aiContext") }}</th>
                    <th>{{ $t("aiModelContext.status") }}</th>
                    <th>{{ $t("aiModelContext.actions") }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="model in models" :key="model._id">
                    <td>
                      <div class="d-flex align-items-center">
                        <i
                          :class="model.icon || 'fas fa-database'"
                          class="me-2 text-primary"
                        ></i>
                        <strong>{{ model.modelName }}</strong>
                      </div>
                    </td>
                    <td>{{ model.label || $t("aiModelContext.noLabel") }}</td>
                    <td>
                      <div v-if="isModelAiConfigured(model)" class="text-success">
                        <i class="fas fa-check-circle me-1"></i>
                        {{ $t("aiModelContext.configured") }}
                      </div>
                      <div v-else class="text-muted">
                        <i class="fas fa-times-circle me-1"></i>
                        {{ $t("aiModelContext.notConfigured") }}
                      </div>
                    </td>
                    <td>
                      <span class="badge bg-success">{{ $t("aiModelContext.active") }}</span>
                    </td>
                    <td>
                      <button
                        @click="editModelContext(model)"
                        class="btn btn-sm btn-outline-primary me-1"
                      >
                        <i class="fas fa-edit me-1"></i>
                        {{ $t("aiModelContext.configure") }}
                      </button>
                      <button
                        v-if="isModelAiConfigured(model)"
                        @click="viewModelContext(model)"
                        class="btn btn-sm btn-outline-info"
                      >
                        <i class="fas fa-eye me-1"></i>
                        {{ $t("aiModelContext.view") }}
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal para editar contexto -->
    <div
      v-if="showEditModal"
      class="modal fade show"
      tabindex="-1"
      style="display: block; background-color: rgba(0, 0, 0, 0.5)"
    >
      <div class="modal-dialog modal-xl modal-dialog-scrollable ai-context-modal">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="fas fa-robot me-2"></i>
              {{ $t("aiModelContext.configureAIContext") }}
            </h5>
            <button
              type="button"
              class="btn-close"
              @click="closeModal"
            ></button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="saveContext">
              <div class="mb-3">
                <label class="form-label">
                  <strong>{{ $t("aiModelContext.modelLabel") }}</strong>
                </label>
                <div class="form-control-plaintext">
                  <i
                    :class="editingModel.icon || 'fas fa-database'"
                    class="me-2 text-primary"
                  ></i>
                  {{ editingModel.modelName }} -
                  {{ editingModel.label || $t("aiModelContext.noLabel") }}
                </div>
              </div>

              <div class="mb-4">
                <label for="aiContext" class="form-label">
                  <strong>{{ $t("aiModelContext.aiContextGeneralLabel") }}</strong>
                </label>
                <textarea
                  id="aiContext"
                  v-model="contextForm.aiContext"
                  class="form-control"
                  rows="6"
                  :placeholder="$t('aiModelContext.aiContextPlaceholder')"
                ></textarea>
                <div class="form-text">
                  {{ $t("aiModelContext.generalContextOptional") }}
                </div>
                <div class="form-text mt-2">
                  <strong>{{ $t("aiModelContext.contextExamples") }}</strong>
                  <ul class="mt-2 mb-0">
                    <li>"{{ $t("aiModelContext.example1") }}"</li>
                    <li>"{{ $t("aiModelContext.example2") }}"</li>
                    <li>"{{ $t("aiModelContext.example3") }}"</li>
                  </ul>
                </div>
              </div>

              <hr class="my-4" />

              <h6 class="mb-3">
                <i class="fas fa-bolt me-2 text-primary"></i>
                {{ $t("aiModelContext.actionPromptsTitle") }}
              </h6>
              <div class="row">
                <div class="col-md-6 mb-3">
                  <label class="form-label small text-muted mb-1">
                    {{ $t("aiModelContext.actionEditItemLabel") }}
                  </label>
                  <textarea
                    v-model="contextForm.aiActionPrompts.editItem"
                    class="form-control"
                    rows="4"
                    :placeholder="$t('aiModelContext.actionPromptPlaceholder')"
                  ></textarea>
                </div>
                <div class="col-md-6 mb-3">
                  <label class="form-label small text-muted mb-1">
                    {{ $t("aiModelContext.actionAddItemLabel") }}
                  </label>
                  <textarea
                    v-model="contextForm.aiActionPrompts.addItem"
                    class="form-control"
                    rows="4"
                    :placeholder="$t('aiModelContext.actionPromptPlaceholder')"
                  ></textarea>
                </div>
                <div class="col-md-6 mb-3">
                  <label class="form-label small text-muted mb-1">
                    {{ $t("aiModelContext.actionDeleteItemLabel") }}
                  </label>
                  <textarea
                    v-model="contextForm.aiActionPrompts.deleteItem"
                    class="form-control"
                    rows="4"
                    :placeholder="$t('aiModelContext.actionPromptPlaceholder')"
                  ></textarea>
                </div>
                <div class="col-md-6 mb-3">
                  <label class="form-label small text-muted mb-1">
                    {{ $t("aiModelContext.actionSearchItemLabel") }}
                  </label>
                  <textarea
                    v-model="contextForm.aiActionPrompts.searchItem"
                    class="form-control"
                    rows="4"
                    :placeholder="$t('aiModelContext.actionPromptPlaceholder')"
                  ></textarea>
                </div>
              </div>

              <hr class="my-4" />

              <h6 class="mb-2">
                <i class="fas fa-list-ul me-2 text-primary"></i>
                {{ $t("aiModelContext.fieldPromptsTitle") }}
              </h6>
              <p class="text-muted small mb-3">
                {{ $t("aiModelContext.fieldPromptsHint") }}
              </p>
              <div v-if="!fieldPromptRows.length" class="alert alert-light border mb-0">
                {{ $t("aiModelContext.noFieldsInModel") }}
              </div>
              <div v-else class="field-prompt-scroll border rounded p-2 bg-light">
                <div
                  v-for="row in fieldPromptRows"
                  :key="row.path"
                  class="mb-3 pb-3 border-bottom border-secondary-subtle last-border-0"
                >
                  <div class="d-flex flex-wrap justify-content-between align-items-baseline gap-2 mb-1">
                    <code class="small user-select-all">{{ row.path }}</code>
                    <span class="small text-muted">{{ row.label }}</span>
                  </div>
                  <textarea
                    v-model="row.prompt"
                    class="form-control form-control-sm"
                    rows="2"
                    :placeholder="$t('aiModelContext.fieldPromptPlaceholder')"
                  ></textarea>
                </div>
              </div>

              <div class="mt-4">
                <div class="alert alert-warning mb-0">
                  <h6 class="mb-2">
                    <i class="fas fa-lightbulb me-2"></i>{{ $t("aiModelContext.tipsForGoodContext") }}
                  </h6>
                  <ul class="mb-0 small">
                    <li>{{ $t("aiModelContext.tip1") }}</li>
                    <li>{{ $t("aiModelContext.tip2") }}</li>
                    <li>{{ $t("aiModelContext.tip3") }}</li>
                    <li>{{ $t("aiModelContext.tip4") }}</li>
                  </ul>
                </div>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeModal">
              {{ $t("aiModelContext.cancel") }}
            </button>
            <button
              type="button"
              class="btn btn-primary"
              @click="saveContext"
              :disabled="!canSaveContext || saving"
            >
              <i class="fas fa-save me-2"></i>
              {{ saving ? $t("aiModelContext.saving") : $t("aiModelContext.saveContext") }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal para ver contexto -->
    <div
      v-if="showViewModal"
      class="modal fade show"
      tabindex="-1"
      style="display: block; background-color: rgba(0, 0, 0, 0.5)"
    >
      <div class="modal-dialog modal-xl modal-dialog-scrollable ai-context-modal">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="fas fa-eye me-2"></i>
              {{ $t("aiModelContext.modelAIContext") }}
            </h5>
            <button
              type="button"
              class="btn-close"
              @click="closeViewModal"
            ></button>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label class="form-label">
                <strong>{{ $t("aiModelContext.modelLabel") }}</strong>
              </label>
              <div class="form-control-plaintext">
                <i
                  :class="viewingModel.icon || 'fas fa-database'"
                  class="me-2 text-primary"
                ></i>
                {{ viewingModel.modelName }} -
                {{ viewingModel.label || $t("aiModelContext.noLabel") }}
              </div>
            </div>

            <div class="mb-3">
              <label class="form-label">
                <strong>{{ $t("aiModelContext.previewAsSentToAi") }}</strong>
              </label>
              <div
                class="form-control-plaintext view-context-block"
                style="white-space: pre-wrap"
              >
                {{ composePreviewFromModel(viewingModel) || $t("aiModelContext.emptyPreview") }}
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              @click="closeViewModal"
            >
              {{ $t("aiModelContext.close") }}
            </button>
            <button
              type="button"
              class="btn btn-primary"
              @click="editModelContext(viewingModel)"
            >
              <i class="fas fa-edit me-2"></i>
              {{ $t("aiModelContext.edit") }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Overlay para los modales -->
    <div
      v-if="showEditModal || showViewModal"
      class="modal-backdrop fade show"
      style="
        display: block;
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(0, 0, 0, 0.5);
        z-index: 1040;
      "
    ></div>
  </div>
</template>

<script>
import { wencesApi } from "@/api/wencesApi";
import Swal from "sweetalert2";
import ContentSkeleton from "@/components/common/ContentSkeleton.vue";

const ACTION_KEYS = ["editItem", "addItem", "deleteItem", "searchItem"];

export default {
  name: "AIModelContext",
  components: { ContentSkeleton },
  data() {
    return {
      models: [],
      loading: false,
      saving: false,
      showEditModal: false,
      showViewModal: false,
      editingModel: null,
      viewingModel: null,
      fieldPromptRows: [],
      contextForm: {
        aiContext: "",
        aiActionPrompts: {
          editItem: "",
          addItem: "",
          deleteItem: "",
          searchItem: "",
        },
      },
    };
  },
  computed: {
    canSaveContext() {
      return this.hasAnyContextInput();
    },
  },
  async mounted() {
    await this.loadModels();
  },
  methods: {
    trim(v) {
      return v != null ? String(v).trim() : "";
    },

    isModelAiConfigured(model) {
      if (!model) return false;
      if (this.trim(model.aiContext)) return true;
      const ap = model.aiActionPrompts;
      if (ap && typeof ap === "object") {
        for (const k of ACTION_KEYS) {
          if (this.trim(ap[k])) return true;
        }
      }
      const fps = model.aiFieldPrompts;
      if (Array.isArray(fps)) {
        for (const row of fps) {
          if (row && this.trim(row.prompt)) return true;
        }
      }
      return false;
    },

    hasAnyContextInput() {
      if (this.trim(this.contextForm.aiContext)) return true;
      for (const k of ACTION_KEYS) {
        if (this.trim(this.contextForm.aiActionPrompts[k])) return true;
      }
      for (const row of this.fieldPromptRows) {
        if (this.trim(row.prompt)) return true;
      }
      return false;
    },

    /**
     * Recorre fields del ModelDefinition (grupos y arrays de objetos).
     * @param {Array} fields
     * @param {string} prefix
     * @returns {Array<{ path: string, label: string }>}
     */
    flattenFieldPaths(fields, prefix = "") {
      const out = [];
      if (!fields || !Array.isArray(fields)) return out;
      for (const f of fields) {
        if (!f || !f.key) continue;
        const path = prefix ? `${prefix}.${f.key}` : f.key;
        const label = f.label || f.key;
        if (f.type === "group" && Array.isArray(f.fields) && f.fields.length) {
          out.push(...this.flattenFieldPaths(f.fields, path));
        } else if (Array.isArray(f.itemFields) && f.itemFields.length) {
          const arrPrefix = `${path}[]`;
          out.push(...this.flattenFieldPaths(f.itemFields, arrPrefix));
        } else {
          out.push({ path, label });
        }
      }
      return out;
    },

    buildFieldPromptRows(model) {
      const paths = this.flattenFieldPaths(model.fields || []);
      const byKey = new Map();
      for (const r of model.aiFieldPrompts || []) {
        if (r && r.fieldKey) byKey.set(r.fieldKey, this.trim(r.prompt));
      }
      return paths.map((p) => ({
        path: p.path,
        label: p.label,
        prompt: byKey.get(p.path) || "",
      }));
    },

    defaultActionPrompts() {
      return {
        editItem: "",
        addItem: "",
        deleteItem: "",
        searchItem: "",
      };
    },

    composePreviewFromModel(model) {
      if (!model) return "";
      const actionLabelKey = {
        editItem: "actionEditItemLabel",
        addItem: "actionAddItemLabel",
        deleteItem: "actionDeleteItemLabel",
        searchItem: "actionSearchItemLabel",
      };
      const parts = [];
      const g = this.trim(model.aiContext);
      if (g) {
        parts.push(`### ${this.$t("aiModelContext.previewGeneral")}\n${g}`);
      }
      const ap = model.aiActionPrompts || {};
      const actionPieces = [];
      for (const k of ACTION_KEYS) {
        const t = this.trim(ap[k]);
        if (!t) continue;
        const lk = actionLabelKey[k];
        actionPieces.push(
          `#### ${lk ? this.$t(`aiModelContext.${lk}`) : k}\n${t}`
        );
      }
      if (actionPieces.length) {
        parts.push(
          `### ${this.$t("aiModelContext.previewActions")}\n\n${actionPieces.join("\n\n")}`
        );
      }
      const fps = Array.isArray(model.aiFieldPrompts) ? model.aiFieldPrompts : [];
      const fieldLines = fps
        .filter((r) => r && this.trim(r.fieldKey) && this.trim(r.prompt))
        .map((r) => `- "${this.trim(r.fieldKey)}": ${this.trim(r.prompt)}`);
      if (fieldLines.length) {
        parts.push(`### ${this.$t("aiModelContext.previewFields")}\n${fieldLines.join("\n")}`);
      }
      return parts.join("\n\n").trim();
    },

    async loadModels() {
      try {
        this.loading = true;
        const token = localStorage.getItem("token");
        const response = await wencesApi.get("/api/gotrading/model-definitions", {
          params: { raw: "true" },
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.data && Array.isArray(response.data)) {
          this.models = response.data.sort((a, b) => a.order - b.order);
        }
      } catch (error) {
        console.error("Error loading models:", error);
        Swal.fire({
          title: this.$tn("aiModelContext.error"),
          text: this.$tn("aiModelContext.errorLoadingModels"),
          icon: "error",
        });
      } finally {
        this.loading = false;
      }
    },

    editModelContext(model) {
      this.editingModel = model;
      this.contextForm.aiContext = model.aiContext || "";
      this.contextForm.aiActionPrompts = {
        ...this.defaultActionPrompts(),
        ...(model.aiActionPrompts && typeof model.aiActionPrompts === "object"
          ? model.aiActionPrompts
          : {}),
      };
      for (const k of ACTION_KEYS) {
        if (this.contextForm.aiActionPrompts[k] == null) {
          this.contextForm.aiActionPrompts[k] = "";
        }
      }
      this.fieldPromptRows = this.buildFieldPromptRows(model);
      this.showEditModal = true;
      this.showViewModal = false;
    },

    viewModelContext(model) {
      this.viewingModel = model;
      this.showViewModal = true;
      this.showEditModal = false;
    },

    async saveContext() {
      if (!this.hasAnyContextInput()) {
        Swal.fire({
          title: this.$tn("aiModelContext.error"),
          text: this.$tn("aiModelContext.saveRequiresOneSection"),
          icon: "warning",
        });
        return;
      }

      try {
        this.saving = true;
        const token = localStorage.getItem("token");

        const aiFieldPrompts = this.fieldPromptRows
          .filter((r) => this.trim(r.prompt))
          .map((r) => ({
            fieldKey: r.path,
            prompt: this.trim(r.prompt),
          }));

        const aiActionPrompts = {};
        for (const k of ACTION_KEYS) {
          aiActionPrompts[k] = this.trim(this.contextForm.aiActionPrompts[k]);
        }

        const payload = {
          aiContext: this.trim(this.contextForm.aiContext),
          aiActionPrompts,
          aiFieldPrompts,
        };

        const { data } = await wencesApi.put(
          `/api/gotrading/model-definitions/id/${this.editingModel._id}`,
          payload,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        const index = this.models.findIndex((m) => m._id === this.editingModel._id);
        if (index !== -1 && data) {
          this.models.splice(index, 1, { ...this.models[index], ...data });
        }

        Swal.fire({
          title: this.$tn("aiModelContext.success"),
          text: this.$tn("aiModelContext.contextSavedSuccessfully"),
          icon: "success",
          timer: 2000,
          showConfirmButton: false,
        });

        this.closeModal();
      } catch (error) {
        console.error("Error saving context:", error);
        Swal.fire({
          title: this.$tn("aiModelContext.error"),
          text: this.$tn("aiModelContext.errorSavingContext"),
          icon: "error",
        });
      } finally {
        this.saving = false;
      }
    },

    closeModal() {
      this.showEditModal = false;
      this.editingModel = null;
      this.fieldPromptRows = [];
      this.contextForm.aiContext = "";
      this.contextForm.aiActionPrompts = this.defaultActionPrompts();
    },

    closeViewModal() {
      this.showViewModal = false;
      this.viewingModel = null;
    },
  },
};
</script>

<style scoped>
.table th {
  background-color: #f8f9fa;
  border-top: none;
}

.form-control-plaintext {
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  padding: 0.375rem 0.75rem;
}

.ai-context-modal {
  max-width: min(1140px, 96vw);
}

.field-prompt-scroll {
  max-height: 360px;
  overflow-y: auto;
}

.view-context-block {
  min-height: 200px;
  max-height: 55vh;
  overflow-y: auto;
}

.field-prompt-scroll .border-bottom:last-child {
  border-bottom: none !important;
  padding-bottom: 0 !important;
  margin-bottom: 0 !important;
}
</style>
